// Общие константы и утилиты для вкладок раздела «Игры».
//
// Лежат в отдельном файле, потому что нужны всем трём вкладкам:
// «Аналитика» переиспользует подписи статусов и форматирование чисел,
// «Партии» — то же самое, «Награды» — только стили полей.

// Статусы игровой сессии — соответствуют enum в game_sessions.status.
export const STATUS_LABELS = {
  all: 'Все',
  finished: 'Завершена',
  active: 'Идёт',
  abandoned: 'Брошена',
  expired: 'Истекла',
};

export const STATUS_STYLES = {
  finished: 'bg-success/10 text-success',
  active: 'bg-accent-primary/10 text-accent-primary',
  abandoned: 'bg-text-muted/10 text-text-secondary',
  expired: 'bg-error/10 text-error',
};

// Причины, по которым сервер не засчитал счёт.
export const REJECTION_LABELS = {
  IMPLAUSIBLE: 'неправдоподобный темп',
};

export const PERIODS = [
  { value: '7days', label: '7 дней' },
  { value: '30days', label: '30 дней' },
  { value: '90days', label: '90 дней' },
];

export const numberInputClass =
  'w-full px-3 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary';

export const fieldLabelClass = 'block text-xs font-medium text-text-secondary mb-1';

export const formatNumber = (value) => Number(value || 0).toLocaleString('ru-RU');

export const formatPercent = (value) => `${Number(value || 0).toLocaleString('ru-RU')}%`;

// Формат даты из MySQL -> локальное время, иначе браузер покажет UTC
// и время «уедет» на несколько часов назад.
export const parseDateTime = (value) => {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value) => {
  const date = parseDateTime(value);
  return date ? date.toLocaleString('ru-RU') : '—';
};

// Короткий формат даты для оси графика.
export const formatDayLabel = (value) => {
  const date = parseDateTime(value);
  if (!date) return value;
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
};

// Сколько времени заняла партия: от старта до закрытия сессии.
//
// Считаем только по двум реальным меткам времени. Для активных сессий
// длительность от «сейчас» смотреть нельзя: брошенная сессия может
// висеть сутки, и таймер покажет «30 ч» вместо реальных двух минут.
export const formatDuration = (startedAt, finishedAt) => {
  const start = parseDateTime(startedAt);
  const end = parseDateTime(finishedAt);
  if (!start || !end) return null;

  const total = Math.round((end - start) / 1000);
  if (total < 0) return null;

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) return minutes > 0 ? `${hours}ч ${minutes}мин` : `${hours}ч`;
  if (minutes > 0) return seconds > 0 ? `${minutes}мин ${seconds}сек` : `${minutes}мин`;
  return `${seconds} сек`;
};

// Средняя длительность приходит с сервера в секундах.
export const formatDurationSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  return formatDuration('2026-01-01 00:00:00', formatSecondsAsStamp(seconds)) || '—';
};

const formatSecondsAsStamp = (total) => {
  const d = new Date(Date.UTC(2026, 0, 1, 0, 0, 0) + Number(total) * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours()
  )}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

export const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19 19L14.667 14.667M17 9.5C17 13.6421 13.6421 17 9.5 17C5.35786 17 2 13.6421 2 9.5C2 5.35786 5.35786 2 9.5 2C13.6421 2 17 5.35786 17 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Переключатель периода. Такой же набор значений и вид, как на
// страницах «Подписки», «Рекомендации» и дашборде.
export const PeriodSelector = ({ period, onChange, disabled }) => (
  <div className="flex gap-2">
    {PERIODS.map((item) => (
      <button
        key={item.value}
        onClick={() => onChange(item.value)}
        disabled={disabled}
        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          period === item.value
            ? 'bg-accent-primary text-white'
            : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
        }`}
      >
        {item.label}
      </button>
    ))}
  </div>
);

// Карточка метрики для верхнего ряда аналитики.
export const MetricCard = ({ title, value, subValue, accent }) => (
  <div className="bg-bg-secondary rounded-xl border border-border-color p-4">
    <div className="text-xs text-text-secondary">{title}</div>
    <div className={`text-2xl font-bold mt-1 ${accent ? 'text-accent-primary' : 'text-text-primary'}`}>
      {value}
    </div>
    {subValue ? <div className="text-xs text-text-muted mt-1">{subValue}</div> : null}
  </div>
);

// Заглушка графика, когда за выбранный период нет ни одной строки.
export const EmptyChart = ({ hint }) => (
  <div className="h-72 flex flex-col items-center justify-center text-center px-6">
    <div className="text-text-secondary text-sm">Нет данных за выбранный период</div>
    {hint ? <div className="text-text-muted text-xs mt-1">{hint}</div> : null}
  </div>
);

// Тема всплывающей подскаски и осей — как в существующих графиках админки.
export const chartColors = {
  grid: 'rgba(255,255,255,0.1)',
  axis: 'rgba(255,255,255,0.5)',
  tooltip: {
    backgroundColor: 'rgba(31, 41, 55, 0.95)',
    border: '1px solid rgba(75, 85, 99, 0.5)',
    borderRadius: '8px',
    color: '#fff',
  },
};